import { Box, type BoxProps } from "@chakra-ui/react";

import { nvpTokens, type PrimitiveTone } from "./tokens";

type VisualPrimitiveKind = "glow" | "node" | "edge";

interface VisualPrimitiveProps extends BoxProps {
  readonly primitive: VisualPrimitiveKind;
  readonly tone?: PrimitiveTone;
  readonly intensity?: number;
}

const toneColor: Record<PrimitiveTone, string> = {
  blue: nvpTokens.color.primary.electricBlue,
  pink: nvpTokens.color.primary.neonPink,
  yellow: nvpTokens.color.primary.brightYellow,
  violet: nvpTokens.color.primary.cosmicViolet,
  green: nvpTokens.color.primary.matrixGreen
};

export function VisualPrimitive({
  primitive,
  tone = "blue",
  intensity = 1,
  ...props
}: VisualPrimitiveProps) {
  const color = toneColor[tone];

  if (primitive === "glow") {
    return (
      <Box
        aria-hidden="true"
        position="absolute"
        pointerEvents="none"
        borderRadius="999px"
        bg={`radial-gradient(circle, ${color} 0%, transparent 70%)`}
        opacity={0.18 * intensity}
        {...props}
      />
    );
  }

  if (primitive === "node") {
    return (
      <Box
        aria-hidden="true"
        position="absolute"
        pointerEvents="none"
        h="10px"
        w="10px"
        borderRadius="999px"
        bg="white"
        border="2px solid"
        borderColor={color}
        boxShadow={`0 0 18px ${color}`}
        {...props}
      />
    );
  }

  if (primitive === "edge") {
    return (
      <Box
        aria-hidden="true"
        position="absolute"
        pointerEvents="none"
        h="1px"
        bg={nvpTokens.gradient.edge}
        opacity={0.55 * intensity}
        boxShadow={nvpTokens.glow[tone]}
        {...props}
      />
    );
  }

  return null;
}

export function GlowPrimitive(props: Omit<VisualPrimitiveProps, "primitive">) {
  return <VisualPrimitive primitive="glow" {...props} />;
}

export function NodePrimitive(props: Omit<VisualPrimitiveProps, "primitive">) {
  return <VisualPrimitive primitive="node" {...props} />;
}

export function EdgePrimitive(props: Omit<VisualPrimitiveProps, "primitive">) {
  return <VisualPrimitive primitive="edge" {...props} />;
}
